import { useState, useEffect, useContext } from 'react';
import {
    Paper,
    Box,
    Container,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Link,
} from '@mui/material';
import {
    VerifiedUserOutlined,
    ArrowRight,
    ChatBubbleOutline,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButton,
    SttButtonAccept,
    SttFormCheck,
} from '../../sporttia/all';
import { getPath } from '../Pages';

type User = {
    id: number;
    name: string;
    status: string;
    numSCs: number;
};

export default function AcceptConditions(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, signature } = useParams<{ id: string; signature: string }>();
    const [user, setUser] = useState<User | null>(null);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [acceptEmail, setAcceptEmail] = useState(true);

    useEffect(() => {
        confirmEmail();
    }, []);

    useEffect(() => {
        if (user && user.numSCs > 0) {
            setAcceptPrivacy(true);
        }
    }, [user]);

    /**
     * Confirm email
     */
    function confirmEmail() {
        // We've got signature
        if (id && signature) {
            cxt?.api('PUT', `/users/${id}/emails/confirmations`, {
                params: {
                    signature,
                },
                success: (r: { user?: User }) => {
                    if (r.user) {
                        if (r.user.status === 'ACTIVE') {
                            navigate(getPath('login'), { replace: true });
                        } else {
                            setUser(r.user);
                        }
                    }
                },
            });

            // Not enough data, go to home
        } else {
            navigate(getPath('home'), { replace: true });
        }
    }

    function proceed() {
        // User accept privay check
        if (acceptPrivacy) {
            cxt?.api('PUT', `/users/${id}/confirm`, {
                params: {
                    signature,
                    commOn: acceptEmail,
                },
                success: () => {
                    cxt.showMessage('S', t('page.accept.confirmSuccess'));
                    setTimeout(() => {
                        navigate(getPath('login'), { replace: true });
                    }, 2000);
                },
            });

            // Please, check the privacy check
        } else {
            cxt?.showMessage('E', t('page.accept.mustAccept'));
        }
    }

    if (!user) {
        return <></>;
    }

    return (
        <Container component="main" maxWidth="md">
            <Box mt={5}>
                <Paper>
                    <Box p={4}>
                        <Box>
                            <Box display="flex" alignItems="center">
                                <Box mr={1}>
                                    <VerifiedUserOutlined fontSize="large" />
                                </Box>
                                <Typography variant="h4">
                                    {t('page.accept.title').toUpperCase()}
                                </Typography>
                            </Box>
                            <Separator />
                        </Box>
                        <Typography variant="body1">
                            {`${t('general.hello')}, `}
                            <b>{user.name}</b>
                        </Typography>
                        <Box mt={1} mb={1}>
                            <Typography variant="body1">
                                {t('page.accept.desc')}
                            </Typography>
                        </Box>
                        <Box mt={1} mb={1}>
                            <List dense={false}>
                                <ListItem style={{ padding: 0 }}>
                                    <ListItemIcon>
                                        <ArrowRight />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${t('page.accept.task1')}.`}
                                    />
                                </ListItem>
                                <ListItem style={{ padding: 0 }}>
                                    <ListItemIcon>
                                        <ArrowRight />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${t('page.accept.task2')}.`}
                                    />
                                </ListItem>
                                <ListItem style={{ padding: 0 }}>
                                    <ListItemIcon>
                                        <ArrowRight />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${t('page.accept.task3')}.`}
                                    />
                                </ListItem>
                                <ListItem style={{ padding: 0 }}>
                                    <ListItemIcon>
                                        <ArrowRight />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${t('page.accept.task4')}.`}
                                    />
                                </ListItem>
                            </List>
                        </Box>
                        <Box mt={1} mb={1}>
                            <Typography variant="body1">
                                <b>{t('general.note')}: </b>
                                <span>{t('page.accept.updateCond')}: </span>
                                <Link
                                    href="/privacy"
                                    underline="hover"
                                    color="primary"
                                >
                                    {t('contact.legalConditions')}.
                                </Link>
                            </Typography>
                        </Box>
                        {user.numSCs === 0 && (
                            <Box mt={1} mb={1}>
                                <SttFormCheck
                                    color="primary"
                                    caption={
                                        <>
                                            <b>{t('page.accept.check1')}</b>
                                            &nbsp;
                                            <Link
                                                href="/privacy"
                                                underline="hover"
                                                color="primary"
                                            >
                                                {t('contact.legalConditions')}
                                            </Link>
                                            .
                                        </>
                                    }
                                    checked={acceptPrivacy}
                                    onChange={({
                                        value,
                                    }: {
                                        name: string;
                                        value: unknown;
                                    }) => setAcceptPrivacy(value as boolean)}
                                />
                            </Box>
                        )}
                        <Box mt={3} mb={1}>
                            <SttFormCheck
                                color="primary"
                                caption={
                                    <>
                                        <b>{t('page.accept.check2')}</b>{' '}
                                        <span>
                                            {t('page.accept.check2DescShort')}
                                        </span>
                                        .{' '}
                                        <b>
                                            {t(
                                                'page.accept.notificationsDisabled',
                                            )}
                                        </b>
                                    </>
                                }
                                checked={acceptEmail}
                                onChange={({
                                    value,
                                }: {
                                    name: string;
                                    value: unknown;
                                }) => setAcceptEmail(value as boolean)}
                            />
                        </Box>
                        <Separator />
                        <Box
                            mt={1}
                            mb={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <SttButtonAccept onClick={proceed} />
                            <SttButton
                                caption={t('contact.title')}
                                icon={<ChatBubbleOutline />}
                                size="small"
                                onClick={() => navigate(getPath('contact'))}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}

function Separator(): React.ReactElement {
    return <Box m={4} height={0} borderBottom="1px solid #d4d4d4" />;
}
